/**
 * Everything the 📥 Candidate requests tab shows INSTEAD OF, or above, the queue: the consent gate,
 * the first-load spinner, the two failure states and the two banners.
 *
 * Split out of RequestsTab so the tab file is about the queue and its data flow. Presentational
 * only — every one of these takes its text and its callbacks from the tab.
 */
import { AlertCircle, Loader2, Mail } from 'lucide-react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { COLORS } from './format';
import type { InboundResponse } from '../../types/taActionBoard';

/**
 * The consent gate. Deliberately on the TAB rather than at board open: the other three tabs need no
 * Gmail at all, so asking every recruiter for inbox access just to look at interview feedback would
 * be a consent wall for a feature they may never use. Asked here, in context, the reason is visible
 * at the moment it applies.
 */
export function GmailConsentGate({
  error,
  connecting,
  onConnect,
}: {
  error: string | null;
  connecting: boolean;
  onConnect: () => void;
}) {
  return (
    <div className="rounded-lg border p-6" style={{ borderColor: '#E3E3E3' }}>
      <div className="flex items-start gap-3">
        <Mail className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" />
        <div className="space-y-2 text-sm text-gray-600">
          <p className="font-medium text-gray-800">Connect your Gmail to see candidate questions</p>
          <p>
            This reads only your own inbox, and only messages from people who exist in Lever as
            candidates. Replies are saved as <span className="font-medium">drafts</span> for you to
            send yourself — nothing is ever sent for you.
          </p>
          <p className="text-xs text-gray-500">
            Access lasts for this browser session only; nothing is stored on our side. You can revoke
            it any time in your Google account.
          </p>
          {error && <p className="text-xs text-[#791F1F]">{error}</p>}
          <Button size="sm" onClick={onConnect} loading={connecting}>
            Connect Gmail
          </Button>
        </div>
      </div>
    </div>
  );
}

/** First load of the session only — a return visit renders the cached queue instead. */
export function RequestsLoading() {
  return (
    <div className="flex h-40 items-center justify-center gap-2 text-sm text-gray-500">
      <Loader2 className="h-4 w-4 animate-spin" />
      Reading your inbox for candidate questions…
    </div>
  );
}

/** A failure with nothing to fall back on: the error IS the screen. */
export function RequestsLoadFailed({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 py-10 text-sm text-[#791F1F]">
      <AlertCircle className="h-5 w-5" />
      <div>Could not load your candidate requests: {error}</div>
      <Button variant="outline" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}

/** The proxy could not read the mailbox at all (no grant, wrong account, feature not set up). */
export function RequestsUnavailable({ reason }: { reason: string }) {
  return (
    <div className="rounded-lg border p-6 text-sm text-gray-600" style={{ borderColor: '#E3E3E3' }}>
      <p className="font-medium text-gray-800">Candidate requests are not available yet.</p>
      <p className="mt-1">{reason}</p>
    </div>
  );
}

/** Token lapsed mid-session: everything stays on screen behind a one-click re-grant. */
export function GmailExpiredBanner({
  connecting,
  onReconnect,
}: {
  connecting: boolean;
  onReconnect: () => void;
}) {
  return (
    <div
      className="flex flex-wrap items-center gap-3 rounded-lg p-3 text-sm"
      style={{ backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }}
      role="status"
    >
      <span>Your Gmail access expired. Nothing was lost, reconnect to carry on.</span>
      <Button size="sm" onClick={onReconnect} loading={connecting}>
        Reconnect Gmail
      </Button>
    </div>
  );
}

/**
 * A refresh failed while a list was already on screen.
 *
 * Shown as a banner rather than replacing the queue: this session's last good read is far more
 * useful to the recruiter than an apology, and it is honest as long as it says so.
 */
export function RefreshFailedBanner({
  error,
  retrying,
  onRetry,
}: {
  error: string;
  retrying: boolean;
  onRetry: () => void;
}) {
  return (
    <div
      className="flex flex-wrap items-center gap-3 rounded-lg bg-[#FCDFE4] p-3 text-sm text-[#791F1F]"
      role="status"
    >
      <AlertCircle className="h-4 w-4 shrink-0" />
      <span>Could not refresh just now, so this is your last load. {error}</span>
      <Button size="sm" variant="outline" onClick={onRetry} loading={retrying}>
        Try again
      </Button>
    </div>
  );
}

/** Viewing a colleague's board — the tab still reads YOUR inbox, and must say so. */
export function OtherBoardNotice({ boardFirstName }: { boardFirstName: string }) {
  return (
    <p className="rounded-lg px-3 py-2 text-xs" style={{ backgroundColor: COLORS.cotton, color: '#484848' }}>
      You are viewing {boardFirstName}'s board, but these are the requests in{' '}
      <span className="font-semibold">your own</span> inbox — this tab always reads your Gmail, never
      a colleague's.
    </p>
  );
}

/** The Lever profile an exact-email search resolved to, shown above its (possibly empty) mail. */
export function LeverMatchBanner({ leverMatch }: { leverMatch: InboundResponse['leverMatch'] }) {
  if (!leverMatch) return null;
  return (
    <div
      className="flex flex-wrap items-center gap-3 rounded-xl bg-white p-3 text-sm shadow-sm ring-1 ring-gray-200"
      role="status"
    >
      <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">In Lever</span>
      <span className="font-semibold text-gray-900">{leverMatch.name}</span>
      <span className="text-xs text-gray-500">
        {leverMatch.roleTitle || 'no posting'}
        {leverMatch.stageName ? ` · ${leverMatch.stageName}` : ''}
      </span>
      <a
        href={leverMatch.leverUrl}
        target="_blank"
        rel="noreferrer"
        className="ml-auto text-xs text-blue-600 hover:underline"
      >
        ↗ Open profile
      </a>
    </div>
  );
}
